import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const SummaryContainer = styled.div`
  max-width: 100%;
  padding: 1.5rem;
`;

const SummaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const SummaryTitle = styled.h2`
  margin: 0;
  color: var(--primary-color);
`;

const SummaryStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;

  h3 {
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
    color: #666;
  }

  p {
    margin: 0;
    font-size: 1.5rem;
    font-weight: bold;
    color: ${props => props.status === 'good' ? '#28a745' : props.status === 'warning' ? '#ffc107' : '#dc3545'};
  }
`;

const TestSection = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
  }
`;

const TestList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TestItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }

  span {
    color: ${props => props.status === 'good' ? '#28a745' : props.status === 'warning' ? '#ffc107' : '#dc3545'};
  }
`;

const TestSummary = () => {
  const [summary, setSummary] = useState({
    totalTests: 0,
    passedTests: 0,
    failedTests: 0,
    warningTests: 0,
    sections: {
      security: { total: 0, passed: 0, failed: 0, warnings: 0 },
      performance: { total: 0, passed: 0, failed: 0, warnings: 0 },
      accessibility: { total: 0, passed: 0, failed: 0, warnings: 0 },
      compatibility: { total: 0, passed: 0, failed: 0, warnings: 0 }
    }
  });

  useEffect(() => {
    // Collect test results from all test components
    const collectTestResults = () => {
      // This would be implemented to gather results from all test components
      // For now, we'll use placeholder data
      setSummary({
        totalTests: 156,
        passedTests: 142,
        failedTests: 8,
        warningTests: 6,
        sections: {
          security: { total: 45, passed: 42, failed: 2, warnings: 1 },
          performance: { total: 35, passed: 32, failed: 2, warnings: 1 },
          accessibility: { total: 38, passed: 35, failed: 2, warnings: 1 },
          compatibility: { total: 38, passed: 33, failed: 2, warnings: 3 }
        }
      });
    };

    collectTestResults();
  }, []);

  const getStatus = (passed, failed, warnings) => {
    if (failed > 0) return 'error';
    if (warnings > 0) return 'warning';
    return 'good';
  };

  return (
    <SummaryContainer>
      <SummaryHeader>
        <SummaryTitle>Test Summary Report</SummaryTitle>
      </SummaryHeader>

      <SummaryStats>
        <StatCard status={getStatus(summary.passedTests, summary.failedTests, summary.warningTests)}>
          <h3>Overall Status</h3>
          <p>{summary.passedTests}/{summary.totalTests} Passed</p>
        </StatCard>
        <StatCard status={getStatus(summary.sections.security.passed, summary.sections.security.failed, summary.sections.security.warnings)}>
          <h3>Security Tests</h3>
          <p>{summary.sections.security.passed}/{summary.sections.security.total} Passed</p>
        </StatCard>
        <StatCard status={getStatus(summary.sections.performance.passed, summary.sections.performance.failed, summary.sections.performance.warnings)}>
          <h3>Performance Tests</h3>
          <p>{summary.sections.performance.passed}/{summary.sections.performance.total} Passed</p>
        </StatCard>
        <StatCard status={getStatus(summary.sections.accessibility.passed, summary.sections.accessibility.failed, summary.sections.accessibility.warnings)}>
          <h3>Accessibility Tests</h3>
          <p>{summary.sections.accessibility.passed}/{summary.sections.accessibility.total} Passed</p>
        </StatCard>
      </SummaryStats>

      <TestSection>
        <h3>Security Tests</h3>
        <TestList>
          <TestItem status={getStatus(summary.sections.security.passed, summary.sections.security.failed, summary.sections.security.warnings)}>
            <span>Security Headers</span>
            <span>{summary.sections.security.passed}/{summary.sections.security.total} Passed</span>
          </TestItem>
          <TestItem status={getStatus(summary.sections.security.passed, summary.sections.security.failed, summary.sections.security.warnings)}>
            <span>Authentication</span>
            <span>{summary.sections.security.passed}/{summary.sections.security.total} Passed</span>
          </TestItem>
          <TestItem status={getStatus(summary.sections.security.passed, summary.sections.security.failed, summary.sections.security.warnings)}>
            <span>Firestore Rules</span>
            <span>{summary.sections.security.passed}/{summary.sections.security.total} Passed</span>
          </TestItem>
        </TestList>
      </TestSection>

      <TestSection>
        <h3>Performance Tests</h3>
        <TestList>
          <TestItem status={getStatus(summary.sections.performance.passed, summary.sections.performance.failed, summary.sections.performance.warnings)}>
            <span>Load Time</span>
            <span>{summary.sections.performance.passed}/{summary.sections.performance.total} Passed</span>
          </TestItem>
          <TestItem status={getStatus(summary.sections.performance.passed, summary.sections.performance.failed, summary.sections.performance.warnings)}>
            <span>Resource Optimization</span>
            <span>{summary.sections.performance.passed}/{summary.sections.performance.total} Passed</span>
          </TestItem>
          <TestItem status={getStatus(summary.sections.performance.passed, summary.sections.performance.failed, summary.sections.performance.warnings)}>
            <span>API Response Time</span>
            <span>{summary.sections.performance.passed}/{summary.sections.performance.total} Passed</span>
          </TestItem>
        </TestList>
      </TestSection>

      <TestSection>
        <h3>Accessibility Tests</h3>
        <TestList>
          <TestItem status={getStatus(summary.sections.accessibility.passed, summary.sections.accessibility.failed, summary.sections.accessibility.warnings)}>
            <span>WCAG Compliance</span>
            <span>{summary.sections.accessibility.passed}/{summary.sections.accessibility.total} Passed</span>
          </TestItem>
          <TestItem status={getStatus(summary.sections.accessibility.passed, summary.sections.accessibility.failed, summary.sections.accessibility.warnings)}>
            <span>Screen Reader Support</span>
            <span>{summary.sections.accessibility.passed}/{summary.sections.accessibility.total} Passed</span>
          </TestItem>
          <TestItem status={getStatus(summary.sections.accessibility.passed, summary.sections.accessibility.failed, summary.sections.accessibility.warnings)}>
            <span>Keyboard Navigation</span>
            <span>{summary.sections.accessibility.passed}/{summary.sections.accessibility.total} Passed</span>
          </TestItem>
        </TestList>
      </TestSection>

      <TestSection>
        <h3>Compatibility Tests</h3>
        <TestList>
          <TestItem status={getStatus(summary.sections.compatibility.passed, summary.sections.compatibility.failed, summary.sections.compatibility.warnings)}>
            <span>Browser Support</span>
            <span>{summary.sections.compatibility.passed}/{summary.sections.compatibility.total} Passed</span>
          </TestItem>
          <TestItem status={getStatus(summary.sections.compatibility.passed, summary.sections.compatibility.failed, summary.sections.compatibility.warnings)}>
            <span>Mobile Responsiveness</span>
            <span>{summary.sections.compatibility.passed}/{summary.sections.compatibility.total} Passed</span>
          </TestItem>
          <TestItem status={getStatus(summary.sections.compatibility.passed, summary.sections.compatibility.failed, summary.sections.compatibility.warnings)}>
            <span>Feature Support</span>
            <span>{summary.sections.compatibility.passed}/{summary.sections.compatibility.total} Passed</span>
          </TestItem>
        </TestList>
      </TestSection>
    </SummaryContainer>
  );
};

export default TestSummary; 